import { AdditionalInfoModel, MetricsModel, PartnersModel } from '@/models/console/partners';
import { adminUtilsAPI } from '@/providers/apis/admin-utils/admin-utils';
import { consoleAPI } from '@/providers/apis/console/console';
import { taxonomyAPI } from '@/providers/apis/taxonomy/taxonomy';
import { sessionService } from '@/providers/services/auth/session';
import { PARTNERS_TYPE } from '@/utils/constants';
import { sortByProperty, sortByPropertyLowerCase } from '@/utils/utils';
import { Vue, Component, Watch } from 'vue-property-decorator';
import { VueEditor } from 'vue2-editor';

@Component({
    name: 'partners-edit',
    components: {
        'vue-editor': VueEditor,
    },
})

export default class PartnerEdit extends Vue {

    // -------------------------------------------------------------------
    // Properties

    public get filteredTenantList() {
        return this.tenantList.filter((item: any) => {
            return item.name.toLowerCase().indexOf(this.tenantSearchText.toLowerCase()) > -1;
        });
    }

    private partnerTypes: any = PARTNERS_TYPE;

    private partnerDetails: PartnersModel | any = {
        partner_type: null,
        videos: new Array(1),
    };

    private additionalInfo: AdditionalInfoModel | any = {
        notes: '',
        quotes: new Array(1),
        image_about_project: '',
        what_they_get: '',
        nlc_survey_taken: true,
    };

    private metricsData: MetricsModel | any = {
        data_count: null,
        total_teachers: null,
        total_students: null,
    };

    private selectedTenant: any = {
        name: 'Select tenant',
        id: '',
    };

    private countryList: any = [];

    private stateList: any = {};

    private isSubmitted: boolean = false;

    private selectedCountries: number[] = [];

    private selectedState: number[] = [];

    private isEducatorRoute: boolean = false;

    private tenantList: any = [];

    private tenantSearchText: any = '';

    private displayTenantList: any = [];

    private get session() {
        return sessionService.getSession();
    }

    private isUpdate: boolean = false;

    private get partnerType() {
        return this.$route.query.type;
    }

    private selectedEducatorType = undefined;

    private optionsPartnerType = [
        { value: 'lab_schools', text: 'Lab schools' },
        { value: 'schools', text: 'Schools' },
    ];

    private offset: number = 0;

    private limit: number = 20;

    private tenantDataCount = 0;

    @Watch('selectedCountries')
    private onChangeCountries() {
        this.fetchStateList();
    }

    // --------------------------------------------------------------------------
    // Hooks

    private async mounted() {
        this.partnerDetails.partner_type = this.partnerType;
        this.isEducatorRoute = this.partnerType === 'educators' ? true : false;
        this.fetchCountries();
        const params: any = {
            offset: 0,
            limit: this.limit,
        };
        this.tenantList = await this.getTenantList(params);
        this.$nextTick().then(() => {
            this.scrollEventAttach();
        });
        const partnerId = this.$route.params.id;
        if (partnerId) {
            this.isUpdate = true;
            this.fetchPartnerDetails(partnerId);
        }
    }

    // -----------------------------------------------------------------------------
    // Actions

    private onSubmit() {
        const params: PartnersModel = {
            contact_name: this.partnerDetails.contact_name,
            contact_email: this.partnerDetails.contact_email,
            organization_name: this.partnerDetails.organization_name,
            organisation_head: this.partnerDetails.organisation_head,
            partner_type: this.partnerType === 'educators' ?
                this.selectedEducatorType : this.partnerDetails.partner_type,
            primary_phone: this.partnerDetails.primary_phone,
            website: this.partnerDetails.website,
            intro: this.partnerDetails.intro,
            videos: this.partnerDetails.videos,
            additional_info: this.additionalInfo,
            metrics: this.metricsData,
            logo: '',
            status: 'active',
            is_visible: true,
            address: this.partnerDetails.address,
            states: this.selectedState,
            countries: this.selectedCountries,
            super_admin_name: '',
            super_admin_email: '',
            images: [],
            tenant: this.selectedTenant.id,
        };
        this.isSubmitted = true;
        this.$validator.validate().then((valid) => {
            if (valid) {
                if (this.isUpdate) {
                    consoleAPI.updatePartner(params, this.$route.params.id).then(() => {
                        this.$router.push(`/console/partners/list/type/${this.partnerType}`);
                    });
                    return;
                }
                consoleAPI.insertPartner(params).then(() => {
                    this.$router.push(`/console/partners/list/type/${this.partnerType}`);
                });
            }
        });
    }

    private onAddRows(newArrayList: any) {
        newArrayList.push('');
    }

    private onDeleteRows(remove: any) {
        this.partnerDetails.videos.splice(remove, 1);
    }
    private onDeleteAdditionalInfoRows(remove: any) {
        this.additionalInfo.quotes.splice(remove, 1);
    }


    private onGoBack() {
        // this.$router.push(`/partner-catalog/providers/curriculum`);
        this.$router.push(`/console/partners/list/type/${this.partnerType}`);
    }


    // ----------------------------------------------------------------------------
    // Methods

    private fetchPartnerDetails(partnerId: string) {
        consoleAPI.fetchPartnerDetails(partnerId).then((partner: any) => {
            this.partnerDetails = partner;
            this.selectedCountries = partner.countries.map((item: any) => item.id);
            this.selectedState = partner.states.map((item: any) => item.id);
            this.metricsData = Object.assign(this.metricsData, partner.metrics);
            this.additionalInfo = Object.assign(this.additionalInfo, partner.additional_info);
            this.getExistingTenant();
        });
    }

    private inputValidator(errors: any, keyName: string) {
        return this.isSubmitted && errors.has(keyName);
    }

    private fetchCountries() {
        taxonomyAPI.fetchTaxonomyCountry().then((countries: any) => {
            this.countryList = countries.groups ? countries.groups : [];
        });
    }

    private fetchStateList() {
        const selectedCountries = this.selectedCountries;
        selectedCountries.forEach((countryId: number) => {
            if (!this.stateList[countryId]) {
                taxonomyAPI.fetchTaxonomyState({ id: countryId }).then((states: any) => {
                    const statesList = states.groups ? states.groups : [];
                    this.$set(this.stateList, countryId, statesList);
                });
            }
        });
    }

    private hasSelectedCountry(key: any) {
        return this.selectedCountries.indexOf(parseInt(key, 10)) !== -1;
    }

    private async getTenantList(params: any) {
        if (this.tenantList.length !== 0 && this.offset >= this.tenantDataCount) {
            return this.tenantList;
        }
        const tenantData = await adminUtilsAPI.fetchTenantList(params);
        this.tenantDataCount = tenantData.total_count;
        this.tenantList = this.tenantList.concat(tenantData.list);
        return this.tenantList;
    }

    private selectTenantItem(item: any) {
        const tenantDropdown = (this.$refs as any).tenantDropdown;
        if (tenantDropdown) {
            tenantDropdown.hide(true);
        }
        this.selectedTenant = item;
        this.partnerDetails.tenantId = item.id;
        this.tenantSearchText = '';
    }

    private getExistingTenant() {
        this.selectedTenant = this.tenantList.find((item: any) => item.id === this.partnerDetails.tenant) ||
            { name: 'Select tenant', id: '' };
    }

    private async onScroll() {
        this.offset += this.limit;
        const params: any = {
            offset: this.offset,
            limit: this.limit,
        };
        this.tenantList = await this.getTenantList(params);
    }

    private scrollEventAttach() {
        const tenantDd: any = document.querySelector('#tenantDropdown .dropdown-menu');
        if (tenantDd) {
            tenantDd.addEventListener('scroll', (event: any) => {
                const { scrollHeight, scrollTop, clientHeight } = event.target;
                if (Math.abs(scrollHeight - clientHeight - scrollTop) < 1) {
                    this.onScroll();
                }
            });
        }
    }
}
